import React, { useState, useRef } from 'react';
import { useProducts } from '../hooks/useProducts';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Modal } from '../components/common/Modal';
import type { Product } from '../types';

export const Products: React.FC = () => {
  const { products, loading, createProduct, updateProduct, deleteProduct } = useProducts();
  const { showToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    description: '',
    location: '',
    features: '',
    status: 'available' as Product['status'],
    owner: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        showToast('Produto atualizado com sucesso!', 'success');
      } else {
        await createProduct(productData);
        showToast('Produto cadastrado com sucesso!', 'success');
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      showToast(error.message || 'Erro ao salvar produto', 'error');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      price: product.price.toString(),
      description: product.description,
      location: product.location || '',
      features: product.features || '',
      status: product.status,
      owner: product.owner || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        await deleteProduct(id);
        showToast('Produto excluído com sucesso!', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erro ao excluir produto', 'error');
      }
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      price: '',
      description: '',
      location: '',
      features: '',
      status: 'available',
      owner: '',
    });
  };

  const getStatusLabel = (status: Product['status']) => {
    const labels = {
      'available': 'Disponível',
      'reserved': 'Reservado',
      'sold': 'Vendido'
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: Product['status']) => {
    const classes = {
      'available': 'badge-success',
      'reserved': 'badge-warning',
      'sold': 'badge-danger'
    };
    return classes[status] || 'badge-info';
  };

  if (loading) {
    return <div className="text-center py-8">Carregando produtos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Produtos Imobiliários</h2>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
          + Novo Produto
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">Nenhum produto cadastrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <div key={product.id} className="product-card">
              <div className="product-image mb-4">
                <span className="text-4xl">🏠</span>
              </div>
              <div className="product-info">
                <h3 className="font-medium text-lg mb-2">{product.title}</h3>
                <div className="product-price text-accent font-medium mb-2">
                  R$ {product.price.toLocaleString('pt-BR')}
                </div>
                <p className="product-description text-text-secondary text-sm mb-3">
                  {product.description}
                </p>
                <div className="product-meta flex flex-wrap gap-2 text-xs text-text-muted mb-3">
                  {product.location && <span>📍 {product.location}</span>}
                  {product.features && <span>📋 {product.features}</span>}
                  {product.owner && <span>👤 {product.owner}</span>}
                </div>
                <span className={`badge ${getStatusBadgeClass(product.status)}`}>
                  {getStatusLabel(product.status)}
                </span>
                <div className="product-actions flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(product)}
                  >
                    ✏️
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                  >
                    🗑️
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingProduct ? 'Editar Produto' : 'Novo Produto'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={() => formRef.current?.requestSubmit()}>
              {editingProduct ? 'Atualizar' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form ref={formRef} onSubmit={handleSubmit}>
          <Input
            label="Título"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <Input
            label="Preço"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            required
          />
          <div className="form-group">
            <label className="form-label">Descrição</label>
            <textarea
              className="input w-full"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          <Input
            label="Localização"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          />
          <Input
            label="Características"
            value={formData.features}
            onChange={(e) => setFormData({ ...formData, features: e.target.value })}
          />
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-select w-full"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as Product['status'] })}
            >
              <option value="available">Disponível</option>
              <option value="reserved">Reservado</option>
              <option value="sold">Vendido</option>
            </select>
          </div>
          <Input
            label="Proprietário"
            value={formData.owner}
            onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
          />
        </form>
      </Modal>
    </div>
  );
};
